import React, { useEffect, useState } from 'react'
import { Box } from '@material-ui/core'
import { Amplify } from 'aws-amplify'
import { I18n, Hub } from 'aws-amplify/utils'
import { BrowserRouter } from 'react-router-dom'
import {
  fetchUserAttributes,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth'
import config from './aws-exports.js'
import NowRouter from './routes/NowRouter'
import { germanTranslations } from './utils/lang/AmplifyTranslations'
import UserContext, { User } from './context/User'
import CustomLoginPage from './components/Auth/CustomLoginPage'

// Configure Amplify with aws-exports
Amplify.configure(config)

// Set up German translations
I18n.putVocabularies(germanTranslations)
I18n.setLanguage('de')

function App(): JSX.Element {
  const [user, setUser] = useState<User | undefined>()
  const [loading, setLoading] = useState(true)

  const loadUserData = async (): Promise<void> => {
    try {
      // Get current authenticated user
      const currentUser = await getCurrentUser()
      const attributes = await fetchUserAttributes()
      const session = await fetchAuthSession()

      // Extract user groups from token
      const groups =
        (session.tokens?.idToken?.payload['cognito:groups'] as string[]) || []

      setUser({
        userId: currentUser.userId,
        username: currentUser.username,
        email: attributes.email || '',
        emailVerified: attributes.email_verified === 'true',
        userGroups: groups,
        signInDetails: currentUser.signInDetails,
      })
    } catch (err: any) {
      // User not authenticated - this is normal on first load
      setUser(undefined)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Check auth status on mount
    loadUserData()

    // Listen for auth events
    const hubListenerCancelToken = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          loadUserData()
          break
        case 'signedOut':
          setUser(undefined)
          break
        case 'tokenRefresh':
          loadUserData()
          break
        default:
          break
      }
    })

    return () => hubListenerCancelToken()
  }, [])

  // Show loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <div>Loading...</div>
      </Box>
    )
  }

  // Show login page if not authenticated
  if (!user) {
    return (
      <Box px={12}>
        <CustomLoginPage />
      </Box>
    )
  }

  // Show main app if authenticated
  return (
    <div className="App" style={{ flexGrow: 1 }}>
      <UserContext.Provider
        value={{
          user,
          updateUser: (u: User): void => setUser(u),
        }}
      >
        <BrowserRouter>
          <NowRouter />
        </BrowserRouter>
      </UserContext.Provider>
    </div>
  )
}

export default App
